import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, Instagram, MessageCircle, User } from "lucide-react";
import { BrandMark } from "@/components/motifs";
import { logoFor } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";

const CATEGORIES = ["Rings", "Necklaces", "Earrings", "Bracelets", "Anklets"];

export default function Header() {
  const { count, setOpen } = useCart();
  const { settings } = useStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const navLink = (label, path) => (
    <button
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={() => go(path)}
      className={`font-sans text-[13px] uppercase tracking-[0.15em] transition-colors hover:text-amber-700 ${
        pathname === path ? "text-amber-700" : "text-stone-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-[#E7DFD3] bg-[#FAF7F2]/90 backdrop-blur-md">
      {/* Announcement bar */}
      <div className="gold-gradient text-center">
        <p className="mx-auto max-w-7xl px-4 py-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-stone-900">
          {settings?.announcement || "Free International Shipping · Code: PASSPORT2026"}
        </p>
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-stone-800"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>

        <Link to="/" data-testid="brand-logo" className="mx-auto lg:mx-0">
          <BrandMark src={logoFor(settings, "desktop")} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLink("Home", "/")}
          {navLink("Shop All", "/shop")}
          {navLink("Anti-Tarnish", "/shop?segment=Anti-Tarnish")}
          {navLink("Oxidised", "/shop?segment=Oxidised")}
          {navLink("Gift Finder", "/gift-finder")}
          {navLink("Our Story", "/#story")}
          {navLink("Offers", "/#offers")}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <button
            data-testid="header-shop-now"
            onClick={() => navigate("/shop")}
            className="hidden items-center gap-1.5 rounded-full gold-gradient px-4 py-2 text-[12px] font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-105 sm:flex"
          >
            <ShoppingBag size={15} /> Shop Now
          </button>
          <a
            data-testid="header-whatsapp"
            href={`https://wa.me/${settings?.whatsapp_number || "919999999999"}`}
            target="_blank"
            rel="noreferrer"
            className="hidden text-[#1a9e4b] hover:text-[#25D366] transition-colors sm:block"
            aria-label="WhatsApp"
          >
            <MessageCircle size={20} />
          </a>
          <a
            data-testid="header-instagram"
            href={settings?.instagram_url || "#"}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:block text-stone-700 hover:text-amber-700 transition-colors"
            aria-label="Instagram"
          >
            <Instagram size={20} />
          </a>
          <button
            data-testid="account-link"
            onClick={() => navigate("/account")}
            className="text-stone-800 hover:text-amber-700 transition-colors"
            aria-label="My account"
          >
            <User size={21} />
          </button>
          <button
            data-testid="open-cart-button"
            onClick={() => setOpen(true)}
            className="relative text-stone-800 hover:text-amber-700 transition-colors"
            aria-label="Open bag"
          >
            <ShoppingBag size={22} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full gold-gradient text-[10px] font-semibold text-stone-900"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Category strip (desktop) */}
      <div className="hidden lg:block border-t border-[#EFE8DC]">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-8 px-4 py-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`cat-strip-${c.toLowerCase()}`}
              onClick={() => navigate(`/shop?category=${c}`)}
              className="font-serif text-[15px] text-stone-600 hover:text-amber-700 transition-colors"
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm overflow-y-auto bg-[#2C221E] p-6 text-cream">
            <div className="flex items-center justify-between">
              <BrandMark src={logoFor(settings, "mobile")} className="[&_*]:!text-[#F4E7C5]" />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="text-[#F4E7C5]">
                <X size={24} />
              </button>
            </div>
            <div className="mt-10 flex flex-col gap-1">
              {[
                ["Home", "/"],
                ["Shop All", "/shop"],
                ["Anti-Tarnish", "/shop?segment=Anti-Tarnish"],
                ["Oxidised", "/shop?segment=Oxidised"],
                ["Gift Finder", "/gift-finder"],
                ["Our Story", "/#story"],
                ["Offers", "/#offers"],
              ].map(([label, path]) => (
                <button
                  key={label}
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(path)}
                  className="border-b border-white/10 py-3 text-left font-serif text-xl text-[#F4E7C5]"
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-8 font-mono text-[10px] uppercase tracking-widest text-amber-500/70">Shop by category</p>
            <div className="mt-3 flex flex-col gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`mobile-cat-${c.toLowerCase()}`}
                  onClick={() => go(`/shop?category=${c}`)}
                  className="py-2 text-left text-stone-300"
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              <button
                data-testid="mobile-shop-now"
                onClick={() => go("/shop")}
                className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-3 font-semibold uppercase tracking-wider text-stone-900"
              >
                <ShoppingBag size={18} /> Shop Now
              </button>
              <div className="flex gap-3">
                <a
                  data-testid="mobile-whatsapp"
                  href={`https://wa.me/${settings?.whatsapp_number || "919999999999"}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] py-3 text-sm font-medium text-white"
                >
                  <MessageCircle size={17} /> WhatsApp
                </a>
                <a
                  data-testid="mobile-instagram"
                  href={settings?.instagram_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] py-3 text-sm font-medium text-white"
                >
                  <Instagram size={17} /> Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
